import { Component, ContextType } from 'react';
import styled from 'styled-components';
import AceEditor from 'react-ace';
import { require as aceRequire } from 'ace-builds';
import { ModalPortalContext } from '@ardoq/hooks';
import { AceCompleter, EditorLanguage, getCompleters } from './utils';
import 'ace-builds/src-min-noconflict/mode-javascript';
import 'ace-builds/src-min-noconflict/mode-groovy';
import 'ace-builds/src-min-noconflict/mode-json';
import 'ace-builds/src-min-noconflict/theme-tomorrow';
import 'ace-builds/src-min-noconflict/ext-language_tools';
import 'ace-builds/src-min-noconflict/ext-searchbox';
import { returnZero } from '@ardoq/common-helpers';
import { colors } from '@ardoq/design-tokens';

const startLinesAtZero = {
  // This is a hack. We use an object, because it is truthy, but we return
  // zero, so Ace allows us to have zero as the first number, which normally
  // results in the first number being 1
  toString: returnZero,
};

const isCtrlCmdEnter = (event: KeyboardEvent) => {
  if (!event.ctrlKey && !event.metaKey) return false;
  return event.key === 'Enter';
};

type AceAutocomplete = {
  parentNode: HTMLElement | null;
  popup?: { container: HTMLElement };
};

type AceAutocompleteModule = {
  Autocomplete: { for: (editor: unknown) => AceAutocomplete };
};

/**
 * Ace mounts its autocomplete popup on `document.body` and relies on a
 * `z-index: 200000` rule to clear surrounding UI, which loses to any element in
 * the browser top layer. Inside a top-layer modal the popup therefore has to be
 * mounted in that modal's own portal target instead.
 * See `ext-language_tools.js`: `new AcePopup(this.parentNode || document.body)`.
 */
const mountAutocompleteIn = (editor: unknown, container: HTMLElement): void => {
  const { Autocomplete } = aceRequire(
    'ace/autocomplete'
  ) as AceAutocompleteModule;
  const completer = Autocomplete.for(editor);

  completer.parentNode = container;

  if (
    completer.popup &&
    completer.popup.container.parentElement !== container
  ) {
    container.appendChild(completer.popup.container);
  }
};

// This is a hack to get the default export from the AceEditor module
// when building the esm bundle. See: https://github.com/securingsincity/react-ace/issues/1540
// @ts-expect-error todo
const Editor: typeof AceEditor = AceEditor.default
  ? // @ts-expect-error todo
    AceEditor.default
  : AceEditor;

const StyledEditor: typeof Editor = styled(Editor)`
  width: calc(100% - 2px) !important;
  position: relative;
  border: 1px solid rgb(204, 204, 204);
  border-radius: 3px;
  box-sizing: content-box;

  &:hover {
    border-color: ${colors.borderActionActive};
  }

  &:focus-within {
    box-shadow: 0px 0px 0px 1px ${colors.borderActionActive};
    border-color: ${colors.borderActionActive};
  }
`;

interface CodeEditorProps {
  value: string;
  language: EditorLanguage;
  inputId?: string;
  className?: string;
  readOnly?: boolean;
  minLines?: number;
  maxLines?: number;
  firstLineNumber?: number;
  scaleToContainer?: boolean;
  wrap?: boolean;
  tabIndex?: -1;
  greyOut?: boolean;
  onBlur?: VoidFunction;
  onChange?: (value: string) => void;
  onCommit?: (value: string) => void;
  onRef?: (comp?: CodeEditor) => void;
  onCtrlCmdEnter?: () => void;
}

export class CodeEditor extends Component<CodeEditorProps> {
  static contextType = ModalPortalContext;
  declare context: ContextType<typeof ModalPortalContext>;

  ace: (AceEditor & { editor: any }) | null = null;
  localCompleter: AceCompleter | null = null;
  modeCompleter: AceCompleter | null = null;
  private isCommited = false;

  constructor(props: CodeEditorProps) {
    super(props);
    this.onChange = this.onChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.focus = this.focus.bind(this);
    this.resize = this.resize.bind(this);
    this.isFocused = this.isFocused.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  applyCompleters() {
    if (!this.ace || !this.localCompleter || !this.modeCompleter) return;

    this.ace.editor.completers = getCompleters(
      this.localCompleter,
      this.modeCompleter,
      this.props.language
    );
  }

  /* Appends custom completer to the default */
  componentDidMount() {
    if (!this.ace) return;
    this.props.onRef?.(this);
    const [, localCompleter, modeCompleter]: AceCompleter[] =
      this.ace.editor.completers;
    this.localCompleter = localCompleter;
    this.modeCompleter = modeCompleter;
    this.applyCompleters();
    this.mountAutocomplete();
    document.addEventListener('keydown', this.onKeyDown);
  }

  /**
   * The modal portal target is null on a modal's first render, so the container
   * has to be re-resolved on update rather than only on mount.
   */
  componentDidUpdate() {
    this.mountAutocomplete();
  }

  mountAutocomplete() {
    if (!this.ace) return;
    mountAutocompleteIn(this.ace.editor, this.context ?? document.body);
  }

  componentWillUnmount() {
    this.props.onRef?.(undefined);
    document.removeEventListener('keydown', this.onKeyDown);
  }

  onChange(value: string) {
    this.props.onChange?.(value);
    this.isCommited = true;
  }

  onBlur() {
    this.props.onBlur?.();
    if (!this.isCommited) {
      return;
    }

    this.props.onCommit?.(this.props.value);
    this.isCommited = false;
  }

  focus() {
    if (!this.ace) return;
    this.ace.editor.focus();
  }

  isFocused() {
    if (!this.ace) return;
    return this.ace.editor.textInput.isFocused();
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.isFocused()) return;
    if (this.props.onCtrlCmdEnter && isCtrlCmdEnter(event)) {
      this.props.onCtrlCmdEnter();
    }
  }

  /* The height of the editor is bound to maxLines option, 17 is the height of each line */
  resize(height: number) {
    if (!this.ace || !height) return;
    this.ace.editor.setOption('maxLines', height / 17);
    this.ace.editor.resize();
  }

  setAndResetHistory({ value }: { value: string }) {
    if (!this.ace) return;
    this.ace.editor.session.setValue(value);
    this.props.onChange?.(value);
  }

  render() {
    const firstLineNumber =
      this.props.firstLineNumber === 0
        ? startLinesAtZero
        : this.props.firstLineNumber || 1;

    const sizeProps = getSizeProps(this.props);

    return (
      <StyledEditor
        ref={ace => {
          if (!ace) {
            return;
          }

          if (this.props.greyOut) {
            ace.editor.container.style.backgroundColor = colors.grey200;
            ace.editor.container.style.color = colors.textDisabled;
          }

          if (this.props.tabIndex) {
            ace.editor.renderer.textarea.tabIndex = this.props.tabIndex;
            ace.editor.container.tabIndex = this.props.tabIndex;
          }

          if (this.props.inputId) {
            ace.editor.renderer.textarea.id = this.props.inputId;
          }

          this.ace = ace;
        }}
        mode={this.props.language}
        theme={'tomorrow'}
        onBlur={this.onBlur}
        onChange={this.onChange}
        fontSize={13}
        showGutter={true}
        showPrintMargin={false}
        highlightActiveLine={false}
        value={this.props.value}
        editorProps={{ $blockScrolling: Infinity }}
        setOptions={{
          enableBasicAutocompletion: true,
          enableLiveAutocompletion: true,
          tabSize: 2,
          wrap: this.props.wrap,
          showLineNumbers: true,
          firstLineNumber: firstLineNumber as number, // It is not really a number
        }}
        readOnly={this.props.readOnly}
        className={this.props.className}
        {...sizeProps}
      />
    );
  }
}

/**
 * Due to how the underlying AceEditor component works, we have to omit
 * minLines and maxLines to have width and height work.
 */
const getSizeProps = (props: CodeEditorProps) => {
  if (props.scaleToContainer) {
    return {
      width: '100%',
      height: '100%',
    };
  }

  return {
    minLines: props.minLines || 5,
    maxLines: props.maxLines || 25,
  };
};
